import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import type { LinkProps } from "@tanstack/react-router";

import { Avatar } from "~/components/Avatar";
import { LogoMark } from "~/components/LogoMark";
import {
  content,
  contentFullBleed,
  main,
  navItem,
  navItemActive,
  navItemIcon,
  navSection,
  navSectionLabel,
  shell,
  sidebar,
  sidebarFooter,
  sidebarFooterAction,
  sidebarFooterBody,
  sidebarFooterMeta,
  sidebarFooterName,
  sidebarLogo,
  sidebarLogoSub,
  sidebarLogoText,
  sidebarNav,
} from "~/components/AppShell.css";

export interface AppShellNavItem {
  /**
   * TanStack Router target. NonNullable since `<Link to={...}>`
   * itself doesn't accept `undefined` — a nav item without a
   * destination makes no sense.
   */
  to: NonNullable<LinkProps["to"]>;
  /** Displayed text. */
  label: string;
  /**
   * Optional icon — rendered left of the label. Accepts a ReactNode
   * (emoji string, SVG, whatever). Prototype uses emoji for v1.
   */
  icon?: ReactNode;
}

export interface AppShellNavSection {
  /** Small-caps section label ("Hub", "Departments", etc.). */
  label: string;
  items: AppShellNavItem[];
}

export interface AppShellUser {
  name: string;
  /** One-line role/meta shown under the name in the sidebar footer. */
  meta?: string;
  initials: string;
  /** Optional avatar background color. */
  avatarBackground?: string;
}

export interface AppShellProps {
  /** Data-driven nav sections shown in the sidebar body. */
  navSections: AppShellNavSection[];
  /** Current user info shown in the sidebar footer. */
  user: AppShellUser;
  /** Sign-out click handler. Optional — omit to hide the action. */
  onSignOut?: () => void;
  /**
   * Page content. The shell provides a max-width wrapper and padding;
   * pages inside should render their own PageHeading + content.
   */
  children: ReactNode;
  /**
   * Drop the content wrapper's 1400px cap and padding for app-pane
   * surfaces that own their frame edge to edge (the Alpha workspace).
   * Default false: document-like pages keep the reading measure.
   */
  fullBleed?: boolean;
}

/**
 * Two-column app shell: fixed navy sidebar on the left, scrollable
 * main content on the right. Nav is data-driven so new sections can
 * be added without touching this component. The sidebar stays
 * stationary while the main column scrolls.
 */
export function AppShell({
  navSections,
  user,
  onSignOut,
  children,
  fullBleed = false,
}: AppShellProps) {
  return (
    <div className={shell}>
      <aside className={sidebar}>
        <div className={sidebarLogo}>
          <LogoMark size={36} />
          <div>
            <div className={sidebarLogoText}>THE MAY FIRM</div>
            <div className={sidebarLogoSub}>Operating System</div>
          </div>
        </div>

        <nav className={sidebarNav} aria-label="Primary">
          {navSections.map((section) => (
            <div key={section.label} className={navSection}>
              <div className={navSectionLabel}>{section.label}</div>
              {section.items.map((item) => (
                <Link
                  key={String(item.to)}
                  to={item.to}
                  className={navItem}
                  activeProps={{ className: `${navItem} ${navItemActive}` }}
                  // "exact" only for the root — otherwise parent items
                  // would never light up when a child route is active.
                  activeOptions={{ exact: item.to === "/" }}
                >
                  {item.icon !== undefined && (
                    <span className={navItemIcon} aria-hidden="true">
                      {item.icon}
                    </span>
                  )}
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>

        <div className={sidebarFooter}>
          <Avatar
            initials={user.initials}
            size={32}
            background={user.avatarBackground}
          />
          <div className={sidebarFooterBody}>
            <span className={sidebarFooterName}>{user.name}</span>
            {user.meta !== undefined && (
              <span className={sidebarFooterMeta}>{user.meta}</span>
            )}
          </div>
          {onSignOut !== undefined && (
            <button
              type="button"
              onClick={onSignOut}
              className={sidebarFooterAction}
              aria-label="Sign out"
            >
              Sign out
            </button>
          )}
        </div>
      </aside>

      <div className={main}>
        <div className={fullBleed ? contentFullBleed : content}>{children}</div>
      </div>
    </div>
  );
}
